// src/templates/empanadaclub/pages/auth/LoginEmpanadaClub.jsx
// Login personalizado para Empanada Club
// Sin header, sin footer, sin registro, solo login

import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import useAuthStore from '../../../../store/authStore';
import { post } from '../../../../helpers/apiHelper';
import Swal from 'sweetalert2';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faLock, faRocket } from '@fortawesome/free-solid-svg-icons';

function LoginEmpanadaClub() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const { user, isAuthenticated, setUser, logout } = useAuthStore(state => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        setUser: state.setUser,
        logout: state.logout
    }));
    const navigate = useNavigate();

    // Redirigir si el usuario ya está autenticado
    useEffect(() => {
        if (isAuthenticated && user) {
            navigate('/dashboard', { replace: true });
        }
    }, [isAuthenticated, user, navigate]);

    // Si está autenticado, mostrar redirección mientras se procesa
    if (isAuthenticated && user) {
        return <Navigate to="/dashboard" replace />;
    }

    const handleResendVerification = async (email) => {
        try {
            await post('auth/resend-verification', { email });
            Swal.fire({
                title: '¡Email Enviado!',
                text: 'Por favor, revisa tu bandeja de entrada y sigue las instrucciones para verificar tu cuenta.',
                icon: 'success',
                confirmButtonText: 'Entendido'
            });
        } catch (error) {
            console.error('Error resending verification email:', error);
            Swal.fire({
                title: 'Error',
                text: 'No se pudo reenviar el email de verificación. Por favor, inténtalo más tarde.',
                icon: 'error',
                confirmButtonText: 'Entendido'
            });
        }
    };

    const handleLogin = async (event) => {
        event.preventDefault();
        setLoading(true);

        try {
            const response = await post('auth/login', { email, password });
            if (response.token) {
                setUser({ userId: response.userId, token: response.token });
                Swal.fire({
                    title: '¡Bienvenido!',
                    text: 'Has iniciado sesión correctamente',
                    icon: 'success',
                    confirmButtonText: 'Continuar'
                });
                navigate('/dashboard');
            } else {
                throw new Error('Invalid login response');
            }
        } catch (error) {
            console.error('Login failed:', error);

            // Verificar si el error es por email no verificado
            if (error.message === 'Email not verified') {
                Swal.fire({
                    title: 'Email no verificado',
                    text: 'Por favor, verifica tu email para poder iniciar sesión. ¿Deseas que te enviemos el email de verificación nuevamente?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Sí, reenviar email',
                    cancelButtonText: 'No, gracias',
                    showCloseButton: true,
                    html: `
            <div class="text-center">
              <p class="mb-4">Por favor, verifica tu email para poder iniciar sesión.</p>
              <p class="mb-4">1. Revisa tu bandeja de entrada</p>
              <p class="mb-4">2. Busca el email de verificación</p>
              <p class="mb-4">3. Haz clic en el enlace de verificación</p>
              <p class="text-sm text-gray-500">¿No has recibido el email? Puedes solicitar uno nuevo.</p>
            </div>
          `
                }).then((result) => {
                    if (result.isConfirmed) {
                        handleResendVerification(email);
                    }
                });
            } else {
                Swal.fire({
                    title: 'Error',
                    text: 'Credenciales incorrectas. Por favor, verifica tu email y contraseña.',
                    icon: 'error',
                    confirmButtonText: 'Entendido'
                });
            }
            logout();
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full">
                {/* Logo */}
                <div className="text-center mb-8">
                    <Link to="/" className="inline-flex items-center space-x-3 mb-6">
                        <img
                            src="/logos/eclub-logo-color.svg"
                            alt="Empanada Club Logo"
                            className="h-12 w-auto"
                        />
                        <span className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                            Empanada Club
                        </span>
                    </Link>
                </div>

                {/* Formulario */}
                <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                    <form className="space-y-6" onSubmit={handleLogin}>
                        {/* Email */}
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                Correo electrónico
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FontAwesomeIcon icon={faEnvelope} className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                                    placeholder="tu@email.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Password */}
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                                Contraseña
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FontAwesomeIcon icon={faLock} className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="password"
                                    name="password"
                                    type="password"
                                    autoComplete="current-password"
                                    required
                                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                                    placeholder="Tu contraseña"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Forgot Password */}
                        <div className="text-right">
                            <Link
                                to="/reset-password"
                                className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200"
                            >
                                ¿Olvidaste tu contraseña?
                            </Link>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faRocket} className="mr-2" />
                                    Iniciar Sesión
                                </>
                            )}
                        </button>
                    </form>

                    {/* Divider */}
                    <div className="mt-6">
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-300" />
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-white text-gray-500">Opciones</span>
                            </div>
                        </div>
                    </div>

                    {/* Link to Franquiciado Login */}
                    <div className="mt-4">
                        <Link
                            to="/franquiciado/login-empanadaclub"
                            className="w-full flex justify-center items-center py-2 px-4 border border-purple-300 rounded-xl shadow-sm text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-300"
                        >
                            <FontAwesomeIcon icon={faRocket} className="mr-2" />
                            Iniciar Sesión como Franquiciado
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default LoginEmpanadaClub;

